import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Calendar, Bell, CheckCircle2, Loader2, X, PartyPopper } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EmpresaPublica = () => {
  const { empresaId } = useParams();
  const navigate = useNavigate();
  const [empresa, setEmpresa] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [formData, setFormData] = useState({
    cliente_nome: '',
    cliente_telefone: '',
    cliente_email: '',
    observacoes: ''
  });

  useEffect(() => {
    fetchEmpresaData();
  }, [empresaId]);

  const fetchEmpresaData = async () => {
    try {
      const response = await axios.get(`${API}/empresa/${empresaId}/info`);
      setEmpresa(response.data);
    } catch (error) {
      toast.error('Empresa não encontrada');
      setTimeout(() => navigate('/'), 3000);
    } finally {
      setLoading(false);
    }
  };

  // Máscara de telefone
  const formatarTelefone = (valor) => {
    // Remove tudo que não é número
    const numeros = valor.replace(/\D/g, '');
    
    // Aplica a máscara (11) 98765-4321
    if (numeros.length <= 2) {
      return numeros;
    } else if (numeros.length <= 7) {
      return `(${numeros.slice(0, 2)}) ${numeros.slice(2)}`;
    } else if (numeros.length <= 11) {
      return `(${numeros.slice(0, 2)}) ${numeros.slice(2, 7)}-${numeros.slice(7, 11)}`;
    }
    
    // Limita a 11 dígitos
    return `(${numeros.slice(0, 2)}) ${numeros.slice(2, 7)}-${numeros.slice(7, 11)}`;
  };

  const handleTelefoneChange = (e) => {
    const valorFormatado = formatarTelefone(e.target.value);
    setFormData({...formData, cliente_telefone: valorFormatado});
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validar telefone (mínimo 11 dígitos)
    const numerosSomente = formData.cliente_telefone.replace(/\D/g, '');
    if (numerosSomente.length < 11) {
      toast.error('Telefone deve ter 11 dígitos (DDD + número)');
      return;
    }
    
    setSubmitting(true);

    try {
      await axios.post(`${API}/empresa/${empresaId}/lembrete`, formData);

      // Mostrar modal de sucesso
      setShowSuccessModal(true);
      
      // Reset form
      setFormData({
        cliente_nome: '',
        cliente_telefone: '',
        cliente_email: '',
        observacoes: ''
      });
    } catch (error) {
      toast.error('Erro ao cadastrar lembrete. Tente novamente.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCloseModal = () => {
    setShowSuccessModal(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 via-white to-red-50 flex items-center justify-center">
        <div className="text-lg text-gray-600">Carregando...</div>
      </div>
    );
  }

  if (!empresa) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 via-white to-red-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-lg text-gray-600">Empresa não encontrada</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-red-50 via-white to-red-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8">
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-3xl bg-primary mb-4 shadow-lg">
              <span className="text-white text-4xl font-bold">L</span>
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              {empresa.nome_empresa}
            </h1>
            <p className="text-lg text-gray-600">{empresa.tipo_profissao}</p>
            {empresa.telefone && (
              <p className="text-sm text-gray-500 mt-2">
                📞 {empresa.telefone}
              </p>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-8">
        {/* Info Card */}
        <Card className="p-6 rounded-2xl border-2 border-red-200 bg-gradient-to-br from-red-50 to-red-50 mb-6">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-xl bg-primary flex items-center justify-center flex-shrink-0">
              <Bell className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900 mb-2">
                Nunca mais perca um prazo importante!
              </h2>
              <p className="text-gray-700 mb-3">
                Cadastre-se e receba alertas automáticos sobre seus compromissos com <strong>{empresa.nome_empresa}</strong>.
              </p>
              <div className="bg-white/50 rounded-xl p-3 border border-red-200">
                <p className="text-sm font-medium text-gray-900">📋 Serviço: {empresa.tipo_profissao}</p>
                <p className="text-xs text-gray-600 mt-1">Você será lembrado automaticamente no período correto</p>
              </div>
            </div>
          </div>
        </Card>

        {/* Form */}
        <Card className="p-8 rounded-2xl border shadow-lg bg-white">
          <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-2">
            <Calendar className="w-6 h-6 text-primary" />
            Cadastrar Lembrete
          </h2>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="grid md:grid-cols-2 gap-5">
              <div>
                <Label htmlFor="cliente_nome">Seu Nome Completo *</Label>
                <Input
                  id="cliente_nome"
                  data-testid="cliente-nome-input"
                  placeholder="Ex: João Silva"
                  value={formData.cliente_nome}
                  onChange={(e) => setFormData({...formData, cliente_nome: e.target.value})}
                  required
                  className="rounded-xl h-11 placeholder:text-gray-300"
                />
              </div>

              <div>
                <Label htmlFor="cliente_telefone">Telefone *</Label>
                <Input
                  id="cliente_telefone"
                  data-testid="cliente-telefone-input"
                  placeholder="Ex: (11) 98765-4321"
                  value={formData.cliente_telefone}
                  onChange={handleTelefoneChange}
                  required
                  maxLength={15}
                  className="rounded-xl h-11 placeholder:text-gray-300"
                />
                <p className="text-xs text-gray-500 mt-1">
                  {formData.cliente_telefone.replace(/\D/g, '').length}/11 dígitos
                </p>
              </div>
            </div>

            <div>
              <Label htmlFor="cliente_email">E-mail (opcional)</Label>
              <Input
                id="cliente_email"
                data-testid="cliente-email-input"
                type="email"
                placeholder="Ex: seu@email.com"
                value={formData.cliente_email}
                onChange={(e) => setFormData({...formData, cliente_email: e.target.value})}
                className="rounded-xl h-11 placeholder:text-gray-300"
              />
            </div>

            <div>
              <Label htmlFor="observacoes">Observações (opcional)</Label>
              <Textarea
                id="observacoes"
                data-testid="observacoes-textarea"
                placeholder="Ex: Preferência de horário, observações sobre tratamento anterior..."
                value={formData.observacoes}
                onChange={(e) => setFormData({...formData, observacoes: e.target.value})}
                className="rounded-xl min-h-24 placeholder:text-gray-300"
              />
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
              <p className="text-sm text-blue-900">
                ℹ️ <strong>Importante:</strong> O prazo de retorno será calculado automaticamente pela {empresa.nome_empresa} conforme suas políticas de atendimento.
              </p>
            </div>

            <Button
              type="submit"
              data-testid="submit-lembrete-button"
              disabled={submitting}
              className="w-full rounded-2xl h-12 bg-primary hover:bg-primary-hover shadow-lg text-lg font-medium"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Cadastrando...
                </>
              ) : (
                <>
                  <Bell className="w-5 h-5 mr-2" />
                  Cadastrar Lembrete
                </>
              )}
            </Button>
          </form>
        </Card>

        {/* Footer */}
        <div className="text-center mt-8 text-sm text-gray-500">
          <p className="mb-2">Powered by <strong className="text-primary">Lembrou?</strong> © 2026</p>
          <p className="text-xs">Sistema Inteligente de Lembretes</p>
        </div>
      </main>

      {/* Modal de Sucesso */}
      {showSuccessModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 animate-in fade-in duration-200">
          <div className="bg-white rounded-3xl shadow-2xl max-w-md w-full p-8 relative animate-in zoom-in duration-300">
            {/* Botão Fechar */}
            <button
              onClick={handleCloseModal}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="w-6 h-6" />
            </button>

            {/* Ícone de Sucesso Animado */}
            <div className="flex justify-center mb-6">
              <div className="relative">
                <div className="w-24 h-24 bg-green-100 rounded-full flex items-center justify-center animate-in zoom-in duration-500">
                  <CheckCircle2 className="w-16 h-16 text-green-600" />
                </div>
                {/* Confetes */}
                <div className="absolute -top-2 -right-2 animate-bounce">
                  <PartyPopper className="w-8 h-8 text-yellow-500" />
                </div>
              </div>
            </div>

            {/* Título */}
            <h2 className="text-3xl font-bold text-gray-900 text-center mb-3">
              Tudo certo! 🎉
            </h2>

            {/* Mensagem */}
            <p className="text-lg text-gray-700 text-center mb-6">
              Seu lembrete foi cadastrado com sucesso!
            </p>

            {/* Informações */}
            <div className="bg-gradient-to-br from-green-50 to-emerald-50 border-2 border-green-200 rounded-2xl p-5 mb-6">
              <div className="flex items-start gap-3">
                <Bell className="w-6 h-6 text-green-600 flex-shrink-0 mt-0.5" />
                <div className="space-y-2">
                  <p className="text-sm font-semibold text-green-900">
                    Você poderá receber lembretes automáticos via:
                  </p>
                  <ul className="text-sm text-green-800 space-y-1">
                    <li>✅ WhatsApp</li>
                    <li>✅ E-mail</li>
                  </ul>
                  <p className="text-xs text-green-700 mt-3 pt-3 border-t border-green-200">
                    Os lembretes serão enviados <strong>30 dias, 15 dias, 7 dias e no dia</strong> do vencimento.
                  </p>
                </div>
              </div>
            </div>

            {/* Agradecimento */}
            <div className="bg-gradient-to-r from-red-50 to-pink-50 border border-red-200 rounded-2xl p-4 mb-6">
              <p className="text-sm text-center text-gray-800">
                <span className="text-red-600 font-bold">Obrigado</span> por confiar na <strong>{empresa.nome_empresa}</strong>!
              </p>
            </div>

            {/* Botão Fechar */}
            <Button
              onClick={handleCloseModal}
              className="w-full rounded-2xl h-12 bg-primary hover:bg-primary-hover text-lg font-medium"
            >
              Entendi, obrigado!
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmpresaPublica;
